import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Alert,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  People,
  AttachMoney,
  Warning,
  Visibility,
  Download,
  Refresh,
  FilterList,
  ReceiptLong,
  CreditCardOff,
  Inbox,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { BillingStats, UserSubscription, BillingInvoice } from '../types/billing';
import { getAuthHeaders } from '../utils/apiConfig';
import { PageContainer, PageHeader, TabShell, TabShellTab } from '../components/ui';
import { useUrlTab } from '../hooks/useUrlTab';

const fmtDate = (v: unknown) => (v ? new Date(v as string).toLocaleString() : '—');

type BillingTabKey = 'overview' | 'subscriptions' | 'invoices';

/** Churn at or below this shows a neutral/positive state; above it the card warns. */
const CHURN_WARN_THRESHOLD = 0;

const BILLING_TAB_KEYS: readonly BillingTabKey[] = ['overview', 'subscriptions', 'invoices'];

const billingTabs: TabShellTab<BillingTabKey>[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'subscriptions', label: 'Subscriptions' },
  { key: 'invoices', label: 'Invoices' },
];

export const AdminBillingPage: React.FC = () => {
  const [selectedTab, setSelectedTab] = useUrlTab(BILLING_TAB_KEYS, 'overview');
  const [viewedInvoice, setViewedInvoice] = useState<any | null>(null);
  const [viewedSubscription, setViewedSubscription] = useState<any | null>(null);

  // Fetch billing stats from API
  const { data: statsData, isLoading: statsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['admin-billing-stats'],
    queryFn: async () => {
      const { getApiUrl } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      const response = await fetch(`${getApiUrl()}/billing/admin/stats`, {
        headers,
      });
      if (!response.ok) {
        throw new Error('Failed to fetch billing stats');
      }
      const result = await response.json();
      return result.data as BillingStats;
    },
  });

  // Fetch subscriptions from API
  const { data: subscriptionsData, isLoading: subscriptionsLoading, refetch: refetchSubscriptions } = useQuery({
    queryKey: ['admin-billing-subscriptions'],
    queryFn: async () => {
      const { getApiUrl } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      const response = await fetch(`${getApiUrl()}/billing/admin/subscriptions`, {
        headers,
      });
      if (!response.ok) {
        throw new Error('Failed to fetch subscriptions');
      }
      const result = await response.json();
      return result.data as UserSubscription[];
    },
  });

  // Fetch invoices from API
  const { data: invoicesData, isLoading: invoicesLoading, refetch: refetchInvoices } = useQuery({
    queryKey: ['admin-billing-invoices'],
    queryFn: async () => {
      const { getApiUrl } = await import('../utils/apiConfig');
      const headers = await getAuthHeaders();
      const response = await fetch(`${getApiUrl()}/billing/admin/invoices`, {
        headers,
      });
      if (!response.ok) {
        throw new Error('Failed to fetch invoices');
      }
      const result = await response.json();
      return result.data as BillingInvoice[];
    },
  });

  // Prefer live data; fall back to empty-safe defaults to avoid runtime errors
  const stats: BillingStats = statsData ?? {
    totalRevenue: 0,
    monthlyRevenue: 0,
    activeSubscriptions: 0,
    trialSubscriptions: 0,
    canceledSubscriptions: 0,
    churnRate: 0,
    averageRevenuePerUser: 0,
    popularPlan: '',
    revenueByPlan: [],
  };
  const subscriptions: UserSubscription[] = subscriptionsData ?? [];
  const invoices: BillingInvoice[] = invoicesData ?? [];
  const loading = statsLoading || subscriptionsLoading || invoicesLoading;

  const formatNumber = (value: any): string => {
    const num = typeof value === 'number' && !isNaN(value) ? value : 0;
    return num.toLocaleString();
  };

  const formatCurrency = (value: any): string => {
    const num = typeof value === 'number' && !isNaN(value) ? value : 0;
    return `$${num.toLocaleString()}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'canceled':
      case 'unpaid':
        return 'error';
      default:
        return 'default';
    }
  };

  const getInvoiceStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'open':
        return 'warning';
      case 'void':
      case 'uncollectible':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleRefreshData = async () => {
    await Promise.all([
      refetchStats(),
      refetchSubscriptions(),
      refetchInvoices(),
    ]);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Billing"
        description="Monitor revenue, subscriptions, and billing analytics."
        actions={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefreshData}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={() => console.log('Export data')}
            >
              Export
            </Button>
          </Box>
        }
      />

      <TabShell tabs={billingTabs} value={selectedTab} onChange={setSelectedTab} aria-label="Billing sections">
      {selectedTab === 'overview' && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {/* Key Metrics */}
          <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AttachMoney color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Monthly Revenue</Typography>
                </Box>
                {loading ? (
                  <Skeleton variant="text" width={100} height={40} />
                ) : (
                  <Typography variant="h4" color="primary">
                    {formatCurrency(stats.monthlyRevenue)}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {stats.payingSubscriptions
                    ? `From ${stats.payingSubscriptions} paying store${stats.payingSubscriptions === 1 ? '' : 's'}`
                    : 'No paying stores yet'}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <People color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Stores on a Paid Plan</Typography>
                </Box>
                {loading ? (
                  <Skeleton variant="text" width={60} height={40} />
                ) : (
                  <Typography variant="h4" color="primary">
                    {formatNumber(stats.activeSubscriptions)}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {[
                    stats.storeCount != null ? `of ${stats.storeCount} stores` : null,
                    stats.trialSubscriptions > 0 ? `${stats.trialSubscriptions} in trial` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'Includes comp and trial plans'}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {stats.churnRate > CHURN_WARN_THRESHOLD ? (
                    <Warning color={stats.churnRate > 10 ? 'error' : 'warning'} sx={{ mr: 1 }} />
                  ) : (
                    <TrendingDown color="success" sx={{ mr: 1 }} />
                  )}
                  <Typography variant="h6">Churn Rate</Typography>
                </Box>
                {loading ? (
                  <Skeleton variant="text" width={60} height={40} />
                ) : (
                  <Typography variant="h4" color={stats.churnRate > 10 ? 'error.main' : stats.churnRate > 5 ? 'warning.main' : 'success.main'}>
                    {formatNumber(stats.churnRate)}%
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {stats.canceledSubscriptions} canceled subscriptions
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">ARPU</Typography>
                </Box>
                {loading ? (
                  <Skeleton variant="text" width={80} height={40} />
                ) : (
                  <Typography variant="h4" color="primary">
                    {formatCurrency(stats.averageRevenuePerUser)}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Avg. monthly revenue per paying store
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <People color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Trial Subscriptions</Typography>
                </Box>
                {loading ? (
                  <Skeleton variant="text" width={60} height={40} />
                ) : (
                  <Typography variant="h4" color="primary">
                    {formatNumber(stats.trialSubscriptions)}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Stores currently in a trial
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AttachMoney color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Total Revenue</Typography>
                </Box>
                {loading ? (
                  <Skeleton variant="text" width={100} height={40} />
                ) : (
                  <Typography variant="h4" color="primary">
                    {formatCurrency(stats.totalRevenue)}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  All-time
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Most Popular Plan</Typography>
                </Box>
                {loading ? (
                  <Skeleton variant="text" width={100} height={40} />
                ) : (
                  <Typography variant="h4" color="primary">
                    {stats.popularPlan || 'N/A'}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  By active subscriptions
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Revenue by Plan */}
          <Box sx={{ flex: '2 1 500px', minWidth: 500 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Revenue by Plan
                </Typography>
                {loading ? (
                  <Stack spacing={1}>
                    <Skeleton variant="rectangular" height={40} />
                    <Skeleton variant="rectangular" height={40} />
                    <Skeleton variant="rectangular" height={40} />
                  </Stack>
                ) : (stats.revenueByPlan || []).length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <ReceiptLong sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      No subscription data available yet
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                      Revenue breakdown will appear once customers subscribe
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Plan</TableCell>
                          <TableCell align="right">Stores</TableCell>
                          <TableCell align="right">Revenue</TableCell>
                          <TableCell align="right">% of Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(stats.revenueByPlan || []).map((plan) => (
                          <TableRow key={plan.planId || plan.planName}>
                            <TableCell>
                              <Chip
                                label={plan.planName}
                                color={plan.planName === stats.popularPlan ? 'primary' : 'default'}
                                variant={plan.planName === stats.popularPlan ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell align="right">{plan.subscribers}</TableCell>
                            <TableCell align="right">{formatCurrency(plan.revenue)}</TableCell>
                            <TableCell align="right">
                              {(() => {
                                const revenue = typeof plan.revenue === 'number' ? plan.revenue : 0;
                                const monthly = typeof stats.monthlyRevenue === 'number' ? stats.monthlyRevenue : 0;
                                const pct = monthly > 0 ? (revenue / monthly) * 100 : 0;
                                return pct.toFixed(1);
                              })()}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {selectedTab === 'subscriptions' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                All Subscriptions ({subscriptions.length})
              </Typography>
              <Button variant="outlined" startIcon={<FilterList />}>
                Filter
              </Button>
            </Box>
            
            {subscriptionsLoading ? (
              <Stack spacing={1}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={52} />
                ))}
              </Stack>
            ) : subscriptions.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CreditCardOff sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No subscriptions yet
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Subscriptions will appear here once customers sign up for paid plans
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Organization</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Period</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subscriptions.map((subscription) => (
                      <TableRow key={subscription.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {(subscription as any).organizationName || subscription.organizationId || subscription.userId}
                          </Typography>
                          {(subscription as any).organizationName && subscription.organizationId && (
                            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                              {subscription.organizationId}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={subscription.plan?.name || subscription.planId || 'Unknown'}
                            variant="outlined"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={subscription.status}
                            color={getStatusColor(subscription.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {subscription.plan?.price 
                            ? `$${subscription.plan.price}/${subscription.plan.interval || 'mo'}`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {subscription.currentPeriodStart && subscription.currentPeriodEnd
                              ? `${new Date(subscription.currentPeriodStart).toLocaleDateString()} - ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="View Details">
                              <IconButton size="small" aria-label="View subscription details" onClick={() => setViewedSubscription(subscription)}>
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {selectedTab === 'invoices' && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Recent Invoices ({invoices.length})
              </Typography>
              <Button variant="outlined" startIcon={<FilterList />}>
                Filter
              </Button>
            </Box>
            
            {invoicesLoading ? (
              <Stack spacing={1}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="rectangular" height={52} />
                ))}
              </Stack>
            ) : invoices.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Inbox sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No invoices yet
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Invoices will appear here once payments are processed through Stripe
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice ID</TableCell>
                      <TableCell>Organization</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                            {invoice.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {(invoice as any).organizationName || invoice.organizationId || invoice.userId || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          ${(invoice.amount || 0).toFixed(2)} {invoice.currency?.toUpperCase() || 'USD'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invoice.status}
                            color={getInvoiceStatusColor(invoice.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {invoice.invoiceDate || invoice.createdAt
                              ? new Date(invoice.invoiceDate || invoice.createdAt).toLocaleDateString()
                              : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="View Details">
                              <IconButton size="small" aria-label="View invoice details" onClick={() => setViewedInvoice(invoice)}>
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={invoice.pdfUrl ? 'Download PDF' : 'No PDF available'}>
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label="Download invoice PDF"
                                  component="a"
                                  href={invoice.pdfUrl || undefined}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  disabled={!invoice.pdfUrl}
                                >
                                  <Download />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
      </TabShell>
      <Dialog open={!!viewedInvoice} onClose={() => setViewedInvoice(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Invoice details</DialogTitle>
        <DialogContent dividers>
          {viewedInvoice && (
            <Stack spacing={1.5}>
              {([
                ['Invoice ID', viewedInvoice.id || '—'],
                ['Organization', viewedInvoice.organizationName || viewedInvoice.organizationId || '—'],
                ['Description', viewedInvoice.description || '—'],
                ['Amount', `$${(viewedInvoice.amount || 0).toFixed(2)} ${viewedInvoice.currency?.toUpperCase() || 'USD'}`],
                ['Status', viewedInvoice.status || '—'],
                ['Invoice date', fmtDate(viewedInvoice.invoiceDate || viewedInvoice.createdAt)],
                ['Due', fmtDate(viewedInvoice.dueDate)],
                ['Paid', fmtDate(viewedInvoice.paidAt)],
                ['Stripe subscription', viewedInvoice.subscriptionId || '—'],
                ['Stripe customer', viewedInvoice.customerId || '—'],
              ] as Array<[string, string]>).map(([label, value]) => (
                <Box key={label}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{value}</Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {viewedInvoice?.invoiceUrl && (
            <Button component="a" href={viewedInvoice.invoiceUrl} target="_blank" rel="noopener noreferrer">
              Open hosted invoice
            </Button>
          )}
          <Button onClick={() => setViewedInvoice(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!viewedSubscription} onClose={() => setViewedSubscription(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Subscription details</DialogTitle>
        <DialogContent dividers>
          {viewedSubscription && (
            <Stack spacing={1.5}>
              {([
                ['Organization', viewedSubscription.organizationName || '—'],
                ['Organization ID', viewedSubscription.organizationId || viewedSubscription.userId || '—'],
                ['Plan', viewedSubscription.plan?.name || viewedSubscription.planId || '—'],
                ['Status', viewedSubscription.status || '—'],
                ['Amount', viewedSubscription.plan?.price ? `$${viewedSubscription.plan.price}/${viewedSubscription.plan.interval || 'mo'}` : '—'],
                ['Current period', viewedSubscription.currentPeriodStart && viewedSubscription.currentPeriodEnd ? `${fmtDate(viewedSubscription.currentPeriodStart)} – ${fmtDate(viewedSubscription.currentPeriodEnd)}` : '—'],
                ['Trial ends', fmtDate(viewedSubscription.trialEnd)],
                ['Cancels at period end', viewedSubscription.cancelAtPeriodEnd ? 'Yes' : 'No'],
                ['Created', fmtDate(viewedSubscription.createdAt)],
                ['Stripe subscription', viewedSubscription.stripeSubscriptionId || '—'],
              ] as Array<[string, string]>).map(([label, value]) => (
                <Box key={label}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{value}</Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {viewedSubscription?.stripeSubscriptionId && (
            <Button onClick={() => window.open(`https://dashboard.stripe.com/subscriptions/${viewedSubscription.stripeSubscriptionId}`, '_blank')}>
              Open in Stripe
            </Button>
          )}
          <Button onClick={() => setViewedSubscription(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};
